const CURRENT_BUILD_VERSION = process.env.REACT_APP_BUILD_VERSION || "unknown";
const CURRENT_BUILD_COMMIT = process.env.REACT_APP_GIT_COMMIT || "unknown";
const VERSION_ENDPOINT = "/version.json";
let guardStarted = false;
let pendingUpdate = null;
let checking = false;
const listeners = new Set();

export function subscribeToFleetUpdate(listener) {
  listeners.add(listener);
  listener(pendingUpdate);
  return () => listeners.delete(listener);
}

export function refreshFleet(build) {
  const url = new URL(window.location.href);
  // A new document URL bypasses cached HTML; hashed bundles and versioned
  // translation URLs fetch the matching assets without clearing sign-in state.
  url.searchParams.set('_fleetUpdate', `${build.commit || build.version}-${Date.now()}`);
  window.location.replace(url.toString());
}

function isLocalHost() {
  if (typeof window === "undefined") return false;
  const hostname = String(window.location.hostname || "").trim().toLowerCase();
  return hostname === "localhost" || hostname === "127.0.0.1";
}

function shouldRunGuard() {
  return process.env.NODE_ENV === "production" && CURRENT_BUILD_VERSION !== "unknown" && !isLocalHost();
}

async function fetchLiveVersion() {
  const cacheBuster = Date.now();
  const response = await fetch(`${VERSION_ENDPOINT}?t=${cacheBuster}`, {
    cache: "no-store",
    headers: {
      "Cache-Control": "no-cache",
      Pragma: "no-cache",
    },
  });

  if (!response.ok) {
    return null;
  }

  const body = await response.json();
  if (typeof body?.version !== "string") {
    return null;
  }

  const commit = typeof body?.commit === "string" ? body.commit : "";
  return {
    version: body.version,
    commit,
    highlights: body.highlights || {},
  };
}

async function checkVersionAndReload() {
  if (checking || document.visibilityState === 'hidden') return;
  checking = true;
  try {
    const liveBuild = await fetchLiveVersion();
    if (!liveBuild) {
      return;
    }

    const currentFingerprint = `${CURRENT_BUILD_VERSION}:${CURRENT_BUILD_COMMIT}`;
    const liveFingerprint = `${liveBuild.version}:${liveBuild.commit || ""}`;

    if (currentFingerprint === liveFingerprint) {
      pendingUpdate = null;
      listeners.forEach(listener => listener(null));
      return;
    }
    if (pendingUpdate?.version === liveBuild.version && pendingUpdate?.commit === liveBuild.commit) return;
    pendingUpdate = liveBuild;
    listeners.forEach(listener => listener(liveBuild));
  } catch (_error) {
    // Offline or a temporarily unavailable manifest must not interrupt work.
  } finally { checking = false; }
}

export function startVersionGuard() {
  if (guardStarted || !shouldRunGuard()) {
    return;
  }

  guardStarted = true;
  const url = new URL(window.location.href);
  if (url.searchParams.has('_fleetUpdate')) {
    url.searchParams.delete('_fleetUpdate');
    window.history.replaceState(window.history.state, '', url.toString());
  }
  checkVersionAndReload();
  window.setInterval(checkVersionAndReload, 60000);
  window.addEventListener('focus', checkVersionAndReload);
  window.addEventListener('online', checkVersionAndReload);
  document.addEventListener('visibilitychange', checkVersionAndReload);
}
